import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AlertCircle, ArrowLeft, CalendarIcon, Gavel, Mail, Lock, Eye, EyeOff, Phone } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import portadaBg from "@/assets/portada-login.jpg";
import logoPlayGrid from "@/assets/logo-PlayGrid-feb26.png";
import { PasswordStrengthIndicator, validatePassword } from '@/components/ui/PasswordStrengthIndicator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface LocationState {
  redirectTo?: string;
  activeTab?: string;
  message?: string;
}

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState<Date | undefined>(undefined);
  const [gender, setGender] = useState<string>('');
  const [phone, setPhone] = useState('');
  const [view, setView] = useState<'signin' | 'signup' | 'forgot-password' | 'reset-password'>('signin');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const locationState = location.state as LocationState | null;
  const isRecoveryMode = useRef(false);

  // Detect recovery token in URL immediately on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('type=recovery') || hash.includes('type=email_change')) {
      isRecoveryMode.current = true;
      setView('reset-password');
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        isRecoveryMode.current = true;
        setView('reset-password');
      } else if (event === 'SIGNED_IN' && session && !isRecoveryMode.current) {
        navigate('/');
      }
    });

    // Solo redirigir si no hay tokens de auth en la URL (recovery, email confirm, etc.)
    const hasAuthParams = window.location.hash.length > 1 || window.location.search.includes('code=');
    if (!hasAuthParams) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) navigate('/');
      });
    }

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePassword(password)) {
      toast({ title: "Contraseña débil", description: "La contraseña debe tener mínimo 8 caracteres, un número y un carácter especial.", variant: "destructive" });
      return;
    }

    if (password !== confirmPassword) {
      toast({ title: "Las contraseñas no coinciden", description: "Por favor verifica que ambas contraseñas sean iguales.", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      if (phone.trim()) {
        const { data: existingPhone } = await supabase.from('profiles').select('user_id').eq('phone', phone.trim()).maybeSingle();
        if (existingPhone) {
          toast({ title: "Teléfono ya registrado", description: "Este número de teléfono ya está asociado a otra cuenta.", variant: "destructive" });
          setLoading(false);
          return;
        }
      }
      const redirectUrl = `${window.location.origin}/`;
      const { error } = await supabase.auth.signUp({
        email, password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: `${firstName} ${lastName}`.trim(),
            first_name: firstName,
            last_name: lastName,
            birth_date: birthDate?.toISOString().split('T')[0],
            gender,
            phone,
          }
        }
      });
      if (error) {
        toast({ title: "Error al registrarse", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "¡Bienvenido!", description: "Tu cuenta ha sido creada." });
      }
    } catch (error) {
      toast({ title: "Error", description: "Ocurrió un error inesperado", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        toast({ title: "Error al iniciar sesión", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "¡Bienvenido!", description: "Has iniciado sesión correctamente." });
        if (locationState?.redirectTo) {
          navigate(locationState.redirectTo, { state: { activeTab: locationState.activeTab || 'dashboard', forceRefresh: true } });
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      toast({ title: "Error", description: "Ocurrió un error inesperado", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth`,
      });
      if (error) throw error;
      toast({ title: "Email enviado", description: "Revisa tu correo para restablecer tu contraseña." });
      setView('signin');
    } catch (error) {
      toast({ title: "Error", description: "No se pudo enviar el email. Inténtalo de nuevo.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword(password)) {
      toast({ title: "Contraseña débil", description: "La contraseña debe tener mínimo 8 caracteres, un número y un carácter especial.", variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: "Las contraseñas no coinciden", description: "Por favor verifica que ambas contraseñas sean iguales.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast({ title: "Contraseña actualizada", description: "Tu contraseña ha sido cambiada correctamente." });
      navigate('/');
    } catch (error) {
      toast({ title: "Error", description: "No se pudo actualizar la contraseña. Inténtalo de nuevo.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const isSignUpDisabled = loading || !validatePassword(password) || password !== confirmPassword || !birthDate || !gender;

  const inputClass = "bg-white/10 border-white/20 text-white placeholder:text-white/60 focus-visible:ring-white/30 focus-visible:border-white/40";
  const labelClass = "text-white/90 text-sm font-medium";

  return (
    <div className="fixed inset-0 flex flex-col overflow-y-auto z-50">
      <img src={portadaBg} alt="" className="fixed inset-0 w-full h-full object-cover" />
      <div className="fixed inset-0 bg-black/50" />

      {view === 'signup' && (
        <button type="button" onClick={() => setView('signin')} className="fixed top-4 left-4 text-white/70 hover:text-white p-2 z-50">
          <ArrowLeft className="h-6 w-6" />
        </button>
      )}

      <div className="relative z-10 flex flex-col min-h-full">
        <div className="flex items-center justify-center pt-8 pb-2">
          <img src={logoPlayGrid} alt="Racket PlayGrid" className="w-48 h-48 object-contain drop-shadow-2xl" />
        </div>

        {/* Form area */}
        <div className="px-6 pb-6 pt-4 w-full max-w-md mx-auto relative z-20">
          {locationState?.message && (
            <div className="mb-4 p-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-[#e7d1b6] flex-shrink-0" />
                <p className="text-sm text-white/90">{locationState.message}</p>
              </div>
            </div>
          )}

          {view === 'reset-password' ? (
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <p className="text-white/80 text-sm text-center mb-2">Ingresa tu nueva contraseña</p>
              <div className="space-y-2">
                <Label htmlFor="new-password" className={labelClass}>Nueva contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="new-password" type={showPassword ? "text" : "password"} placeholder="Nueva contraseña" value={password} onChange={(e) => setPassword(e.target.value)} required className={cn(inputClass, "pl-10 pr-10")} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/70">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <PasswordStrengthIndicator password={password} confirmPassword={confirmPassword} showRequirements={true} />
              <div className="space-y-2">
                <Label htmlFor="confirm-new-password" className={labelClass}>Confirmar nueva contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="confirm-new-password" type={showConfirmPassword ? "text" : "password"} placeholder="Repite la contraseña" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className={cn(inputClass, "pl-10 pr-10")} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/70">
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#e7d1b6] hover:bg-[#d4bc9e] text-gray-900 font-semibold shadow-lg" disabled={loading || !validatePassword(password) || password !== confirmPassword}>
                {loading ? "Guardando..." : "Guardar nueva contraseña"}
              </Button>
            </form>
          ) : view === 'forgot-password' ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <button type="button" onClick={() => setView('signin')} className="flex items-center gap-1 text-white/60 hover:text-white/80 text-sm mb-2">
                <ArrowLeft className="h-4 w-4" /> Volver
              </button>
              <p className="text-white/80 text-sm text-center">Ingresa tu email y te enviaremos un link para restablecer tu contraseña.</p>
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className={labelClass}>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="forgot-email" type="email" placeholder="tu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required className={cn(inputClass, "pl-10")} />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#e7d1b6] hover:bg-[#d4bc9e] text-gray-900 font-semibold shadow-lg" disabled={loading || !email}>
                {loading ? "Enviando..." : "Enviar link de recuperación"}
              </Button>
            </form>
          ) : view === 'signin' ? (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signin-email" className={labelClass}>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signin-email" type="email" placeholder="tu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required className={cn(inputClass, "pl-10")} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="signin-password" className={labelClass}>Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signin-password" type={showPassword ? "text" : "password"} placeholder="Tu contraseña" value={password} onChange={(e) => setPassword(e.target.value)} required className={cn(inputClass, "pl-10 pr-10")} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/70">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={() => setView('forgot-password')} className="text-white/60 text-xs hover:text-white/80 hover:underline">
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#e7d1b6] hover:bg-[#d4bc9e] text-gray-900 font-semibold shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
                disabled={loading}
              >
                {loading ? "Iniciando sesión..." : "Iniciar Sesión"}
              </Button>
              <p className="text-center text-white/70 text-sm mt-4">
                ¿No tienes cuenta?{' '}
                <button type="button" onClick={() => setView('signup')} className="text-[#e7d1b6] font-semibold hover:underline">
                  Crea una aquí
                </button>
              </p>
              <div className="flex items-center gap-3 mt-4">
                <Separator className="flex-1 bg-white/20" />
                <span className="text-white/50 text-xs">o</span>
                <Separator className="flex-1 bg-white/20" />
              </div>
              <Button onClick={() => navigate('/judge')} className="w-full border border-white/30 bg-transparent text-white hover:bg-white/10 mt-3">
                <Gavel className="w-4 h-4 mr-2" /> Soy Juez de Cancha
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSignUp} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="signup-firstname" className={labelClass}>Nombre</Label>
                  <Input id="signup-firstname" type="text" placeholder="Tu nombre" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className={cn(inputClass, "h-12")} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-lastname" className={labelClass}>Apellido</Label>
                  <Input id="signup-lastname" type="text" placeholder="Tu apellido" value={lastName} onChange={(e) => setLastName(e.target.value)} required className={cn(inputClass, "h-12")} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Fecha de Nacimiento</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start text-left font-normal", inputClass, !birthDate && "text-white/60")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {birthDate ? format(birthDate, "PPP", { locale: es }) : "Selecciona fecha"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={birthDate} onSelect={setBirthDate} disabled={(date) => date > new Date() || date < new Date("1900-01-01")} initialFocus captionLayout="dropdown-buttons" fromYear={1900} toYear={new Date().getFullYear()} className="pointer-events-auto" />
                    <div className="flex justify-end p-2 border-t">
                      <Button type="button" size="sm" variant="outline" onClick={() => setCalendarOpen(false)}>
                        OK
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Género</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className={cn("w-full", inputClass)}>
                    <SelectValue placeholder="Selecciona tu género" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Masculino</SelectItem>
                    <SelectItem value="female">Femenino</SelectItem>
                    <SelectItem value="prefer_not_to_say">Prefiero no decir</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-phone" className={labelClass}>Número de teléfono</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signup-phone" type="tel" placeholder="+52 123 456 7890" value={phone} onChange={(e) => setPhone(e.target.value)} className={cn(inputClass, "pl-10")} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-email" className={labelClass}>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signup-email" type="email" placeholder="tu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required className={cn(inputClass, "pl-10")} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-password" className={labelClass}>Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signup-password" type={showPassword ? "text" : "password"} placeholder="Crea una contraseña segura" value={password} onChange={(e) => setPassword(e.target.value)} required className={cn(inputClass, "pl-10 pr-10")} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/70">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <PasswordStrengthIndicator password={password} confirmPassword={confirmPassword} showRequirements={true} />
              <div className="space-y-1.5">
                <Label htmlFor="signup-confirm-password" className={labelClass}>Confirmar Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input id="signup-confirm-password" type={showConfirmPassword ? "text" : "password"} placeholder="Repite tu contraseña" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className={cn(inputClass, "pl-10 pr-10")} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/70">
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#e7d1b6] hover:bg-[#d4bc9e] text-gray-900 font-semibold shadow-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50"
                disabled={isSignUpDisabled}
              >
                {loading ? "Creando cuenta..." : "Crear Cuenta"}
              </Button>
              <p className="text-center text-white/70 text-sm mt-3">
                ¿Ya tienes cuenta?{' '}
                <button type="button" onClick={() => setView('signin')} className="text-[#e7d1b6] font-semibold hover:underline">
                  Inicia sesión aquí
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
